import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PublicHeader } from "@/components/PublicHeader";
import { FaqSection } from "@/components/landing/FaqSection";
import { PhoneShowcase } from "@/components/landing/PhoneShowcase";
import { GovernanceSection } from "@/components/landing/GovernanceSection";
import { StandardsStrip } from "@/components/landing/StandardsStrip";
import { TopHalalStocks } from "@/components/landing/TopHalalStocks";
import { Testimonials } from "@/components/landing/Testimonials";
import { FlipNumber } from "@/components/FlipNumber";
import { useApp } from "@/context/AppContext";
import { CurrencyFlag } from "@/components/CurrencyFlag";
import { HelpChat } from "@/components/support/HelpChat";
import { CountUp } from "@/components/CountUp";
import { Logo, Mark } from "@/components/nav/Brand";
import {
  ArrowRight, ArrowUpRight, ArrowDownRight, ShieldCheck, Vault, Landmark,
  TrendingUp, LineChart, Globe2, Lock, DatabaseBackup, Fingerprint, Wallet as WalletIcon,
  CheckCircle2, Sparkles, Moon, Coins,
} from "lucide-react";

const TICKER = [
  { sym: "AAPL", chg: "+1.24%", up: true }, { sym: "NVDA", chg: "+2.86%", up: true },
  { sym: "2222.SR", chg: "+0.31%", up: true }, { sym: "AMZN", chg: "-0.42%", up: false },
  { sym: "QNBK.QA", chg: "+0.68%", up: true }, { sym: "BTCUSD", chg: "+3.10%", up: true },
  { sym: "TSLA", chg: "-1.05%", up: false }, { sym: "ETHUSD", chg: "+0.94%", up: true },
  { sym: "XAUUSD", chg: "+0.22%", up: true }, { sym: "META", chg: "+0.94%", up: true },
];

const POSITIONS = [
  { sym: "AAPL", name: "Apple", price: "228.52", chg: "+1.24%", up: true, badge: "AA" },
  { sym: "NVDA", name: "NVIDIA", price: "121.40", chg: "+2.86%", up: true, badge: "NV" },
  { sym: "2222.SR", name: "Saudi Aramco", price: "28.65", chg: "+0.31%", up: true, badge: "AR" },
];

const FEATURES = [
  { icon: Globe2, title: "Global stocks & ETFs", body: "Trade thousands of US and international names across 70+ exchanges — whole or fractional shares." },
  { icon: Landmark, title: "GCC markets", body: "Tadawul, ADX, DFM, QE and Boursa Kuwait alongside global markets, all from one account." },
  { icon: Moon, title: "Halal investing", body: "Shariah-screened portfolios and Murabaha commodity plans — certified and transparent." },
  { icon: Vault, title: "USD high-yield savings", body: "Earn a competitive 4.85% USD yield on idle cash while you decide where to invest next." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { lang, t } = useApp();
  const go = () => navigate("/login");
  const arabic = lang === "ar";
  const NAV = [["Trade", "ld_nav_trade", "/product/trade"], ["Invest", "ld_nav_invest", "/product/invest"],
               ["Save", "ld_nav_save", "/product/save"], ["Halal", "ld_nav_halal", "/product/halal"],
               ["Pricing", "ld_nav_pricing", "/fees"]];
  const [ticker, setTicker] = useState(TICKER);

  // Real quotes for the marquee, refreshed quietly; the static list is the fallback.
  useEffect(() => {
    let alive = true;
    const symbols = TICKER.map((t) => t.sym).join(",");
    const load = () => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/market/quotes?symbols=${symbols}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((rows) => {
        if (!alive || !rows?.length) return;
        setTicker(rows.map((q) => ({
          sym: q.symbol,
          chg: `${q.change_pct >= 0 ? "+" : ""}${Number(q.change_pct || 0).toFixed(2)}%`,
          up: Number(q.change_pct || 0) >= 0,
          price: q.price,
        })));
      })
      .catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#F5F3FA] text-[#1A1330]" data-testid="landing-page">
      {/* utility bar */}
      <div className="w-full bg-[#332654] text-white/70 text-[12px]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-9 flex items-center justify-between">
          <span className="hidden sm:flex items-center gap-1.5 text-[color:var(--tharwy-lime)]"><Lock className="h-3 w-3" /> {t("ld_secured")}</span>
          <div className="flex items-center gap-5 ms-auto">
            <button onClick={() => navigate("/help")} data-testid="landing-talk-to-team"
              className="hover:text-white transition-colors hidden sm:inline">{t("ld_talk")}</button>
            <button onClick={() => navigate("/help")} data-testid="landing-help-desk"
              className="hover:text-white transition-colors">{t("ld_helpdesk")}</button>
          </div>
        </div>
      </div>

      <PublicHeader />

      {/* HERO — dark green band with floating dashboard preview */}
      <section className="relative overflow-hidden navy-strip text-white">
        <div className="absolute inset-0 opacity-[0.22] pointer-events-none" aria-hidden="true"
          style={{ backgroundImage: "url(/night-sky.jpg)", backgroundSize: "cover", backgroundPosition: "bottom" }} />
        <div className="absolute inset-0 hero-sheen opacity-70" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 pt-14 sm:pt-20 pb-24 sm:pb-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/80" data-testid="hero-eyebrow">
              <Sparkles className="h-3.5 w-3.5 text-[color:var(--tharwy-lime)]" /> {t("ld_badge")}
            </span>
            <h1 className={`mt-5 text-[2.7rem] leading-[1.05] sm:text-[3.6rem] sm:leading-[1.02] font-bold flip-lines ${arabic ? "" : "tracking-tight"}`} data-testid="hero-title">
              <span className="flip-line" style={{ animationDelay: "0.05s" }}>{t("ld_h1a")}</span>
              <span className="flip-line" style={{ animationDelay: "0.35s" }}>{t("ld_h1b")}</span>
              <span className="flip-line text-[color:var(--tharwy-lime)]" style={{ animationDelay: "0.65s" }}>
                {t("ld_h1c")}
              </span>
            </h1>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-white/70">
              {t("ld_sub")}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Button size="lg" className="rounded-full px-7 h-12 text-base bg-[color:var(--tharwy-lime)] text-[#332654] hover:bg-white" onClick={go} data-testid="hero-get-started">
                {t("ld_cta_open")} <ArrowRight className="h-5 w-5 ms-1.5" />
              </Button>
              <Button size="lg" variant="outline" className="rounded-full px-7 h-12 text-base border-white/25 bg-transparent text-white hover:bg-white/10" onClick={() => navigate("/product/trade")} data-testid="hero-explore">
                {t("ld_cta_explore")}
              </Button>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/60">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-[color:var(--tharwy-lime)]" /> {t("ld_trust_min")}</span>
              <span className="flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[color:var(--tharwy-lime)]" /> {t("ld_trust_shariah")}</span>
              <span className="flex items-center gap-1.5"><Lock className="h-4 w-4 text-[color:var(--tharwy-lime)]" /> {t("ld_trust_enc")}</span>
            </div>
          </div>

          {/* Floating dashboard preview (distinct from a phone mockup) */}
          <div className="lg:col-span-6 relative" data-testid="landing-preview">
            <div className="relative mx-auto max-w-md">
              {/* Balance hero card */}
              <div className="balance-hero rounded-3xl p-6 text-[#332654] shadow-[0_40px_90px_-40px_rgba(0,0,0,0.6)]">
                <div className="relative z-10">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold tracking-[0.14em] uppercase text-[#332654]/70">Total Balance</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold"><span className="h-2 w-2 rounded-full bg-[#332654] live-dot" /> LIVE</span>
                  </div>
                  <CountUp value={180650.90} className="mt-2 block numeric text-4xl font-bold"
                    format={(v) => `$${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                  <div className="mt-1 inline-flex items-center gap-1 text-sm font-semibold"><TrendingUp className="h-4 w-4" /> +$4,212.65 · +2.4% today</div>
                  <div className="mt-4 flex gap-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-semibold text-[#3E2E65]"><ArrowDownRight className="h-4 w-4" /> Deposit</span>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#3E2E65] px-4 py-2 text-sm font-semibold text-white"><ArrowUpRight className="h-4 w-4" /> Invest</span>
                  </div>
                </div>
              </div>

              {/* Positions card floating below-right */}
              <div className="mt-4 rounded-2xl bg-white p-4 text-[#1A1330] shadow-[0_30px_70px_-40px_rgba(0,0,0,0.55)]">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold">Your positions</span>
                  <span className="text-xs font-medium text-[color:var(--tharwy-lime-700)]">See all</span>
                </div>
                <div className="space-y-2">
                  {POSITIONS.map((p) => (
                    <div key={p.sym} className="flex items-center gap-2.5 rounded-xl border border-black/5 px-3 py-2">
                      <div className="h-7 w-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white shrink-0"
                        style={{ background: p.up ? "var(--tharwy-lime)" : "#94A3B8" }}>{p.badge}</div>
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold leading-none">{p.name}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">{p.sym}</div>
                      </div>
                      <div className="text-right">
                        <div className="numeric text-[13px] font-semibold">${p.price}</div>
                        <div className={`numeric text-[10px] font-medium inline-flex items-center gap-0.5 ${p.up ? "text-emerald-600" : "text-red-500"}`}>
                          {p.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{p.chg}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Multi-currency chip */}
              <div className="absolute -left-4 sm:-left-10 -bottom-5 hidden sm:flex items-center gap-2 rounded-2xl bg-white px-3 py-2.5 shadow-[0_24px_60px_-34px_rgba(0,0,0,0.5)]">
                <div className="h-8 w-8 rounded-xl bg-[#EFF6D8] flex items-center justify-center text-[#3E2E65]"><Coins className="h-4 w-4" /></div>
                <div className="text-[#1A1330]">
                  <div className="text-[10px] text-slate-400 leading-none">Accounts</div>
                  <div className="text-[12px] font-semibold">USD · QAR · AED · EUR</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* markets ticker marquee — scrolls right to left, pauses on hover */}
        <div className="relative border-t border-white/10 bg-[#332654]/60">
          <div className="marquee py-3" data-testid="landing-ticker">
            <div className="marquee-track" style={{ "--marquee-duration": "34s" }}>
              {[0, 1].map((dup) => (
                <div key={dup} className="flex items-center gap-6 ps-6" aria-hidden={dup === 1}>
                  {ticker.map((tk) => (
                    <div key={`${dup}-${tk.sym}`} className="flex items-center gap-1.5 shrink-0 text-sm">
                      <span className="font-medium text-white/80">{tk.sym}</span>
                      {tk.price != null && (
                        <FlipNumber value={Number(tk.price)} className="numeric text-xs text-white/50"
                          format={(v) => Number(v).toLocaleString("en-US", { maximumFractionDigits: 2 })} />
                      )}
                      <span className={`numeric text-xs inline-flex items-center gap-0.5 ${tk.up ? "text-[color:var(--tharwy-lime)]" : "text-red-400"}`}>
                        {tk.up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}{tk.chg}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <PhoneShowcase onCta={go} />

      {/* features */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-2xl">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="features-title">Everything you need to grow wealth</h2>
          <p className="mt-3 text-[#1A1330]/65">One account for global trading, GCC markets, halal investing and savings — with the tooling to do it well.</p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f) => (
            <div key={f.title} className="group rounded-2xl border border-black/5 bg-white p-6 transition-shadow hover:shadow-premium" data-testid={`feature-${f.title.split(" ")[0].toLowerCase()}`}>
              <div className="h-11 w-11 rounded-xl flex items-center justify-center text-white mb-4 bg-[#3E2E65]">
                <f.icon className="h-5 w-5" />
              </div>
              <h3 className="text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm text-[#1A1330]/65 leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* multi-currency accounts showcase */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 pb-4 sm:pb-8">
        <div className="rounded-3xl bg-white border border-black/5 overflow-hidden grid lg:grid-cols-2">
          <div className="p-8 sm:p-12">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[#EFF6D8] px-3 py-1 text-xs font-semibold text-[#3E2E65]"><WalletIcon className="h-3.5 w-3.5" /> Multi-currency</span>
            <h2 className="mt-4 text-2xl sm:text-3xl font-bold tracking-tight">Open an account in your own currency</h2>
            <p className="mt-3 text-[#1A1330]/65 max-w-lg">Based in Doha, Dubai, Riyadh, London or beyond? Open a cash account in your home currency and hold USD, EUR, GBP and more — fund, receive and withdraw locally without paying FX on every move.</p>
            <ul className="mt-5 space-y-2.5">
              {["Local currency accounts across GCC & global markets", "Convert between currencies at live indicative rates", "Auto-FX top-up when settling a trade in another currency"].map((li) => (
                <li key={li} className="flex items-start gap-2 text-sm text-[#1A1330]/80"><CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-600 shrink-0" />{li}</li>
              ))}
            </ul>
            <Button className="mt-6 rounded-full px-6" onClick={go} data-testid="multicurrency-cta">Open your account <ArrowRight className="h-4 w-4 ms-1.5" /></Button>
          </div>
          <div className="relative bg-[#F5F3FA] p-8 sm:p-12 flex items-center justify-center">
            <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
              {[["USD", "US Dollar"], ["QAR", "Qatari Riyal"], ["AED", "UAE Dirham"], ["EUR", "Euro"], ["GBP", "British Pound"], ["SAR", "Saudi Riyal"]].map(([c, n]) => (
                <div key={c} data-testid={`currency-card-${c}`}
                  className="rounded-2xl bg-white border border-black/5 p-4 shadow-[0_18px_45px_-30px_rgba(26,76,49,0.35)]">
                  <div className="flex items-center gap-2.5">
                    <CurrencyFlag code={c} size={32} />
                    <div><div className="text-sm font-semibold">{c}</div><div className="text-[10px] text-slate-400">{n}</div></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <HelpChat />

      <StandardsStrip />

      {/* FREE HALAL SCREENER */}
      <TopHalalStocks />

      {/* SHARIAH GOVERNANCE */}
      <GovernanceSection onCta={() => navigate("/shariah-board")} />

      {/* SECURITY — 256-bit SSL encryption */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20" data-testid="security-section">
        <div className="rounded-3xl navy-strip text-white relative overflow-hidden px-6 sm:px-12 py-12 sm:py-14">
          <div className="absolute inset-0 hero-sheen opacity-60" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-medium text-white/80"><ShieldCheck className="h-3.5 w-3.5 text-[color:var(--tharwy-lime)]" /> Security first</span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Bank-grade SSL encryption</h2>
              <p className="mt-4 text-white/75 max-w-lg text-base leading-relaxed" data-testid="ssl-copy">
                Our website and app are secured with <span className="font-semibold text-[color:var(--tharwy-lime)]">{t("ld_trust_enc")}</span> to keep your information safe and backed up continuously.
              </p>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {[[Lock, "256-bit SSL", "Encrypted in transit"], [DatabaseBackup, "Continuous backup", "Your data, always safe"], [Fingerprint, "2FA & biometrics", "Protected access"]].map(([Ic, title, sub]) => (
                <div key={title} className="rounded-2xl bg-white/10 border border-white/10 p-5">
                  <Ic className="h-6 w-6 text-[color:var(--tharwy-lime)]" />
                  <div className="mt-3 font-semibold text-sm">{title}</div>
                  <div className="text-xs text-white/60 mt-0.5">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* client voices */}
      <Testimonials />

      {/* FAQ */}
      <FaqSection onCta={go} />

      {/* stats band */}
      <section className="bg-white border-y border-black/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[["70+", "Global exchanges"], ["5,000+", "Stocks & ETFs"], ["$0", "Account minimum"], ["4.85%", "USD savings APY"]].map(([v, l]) => (
            <div key={l}>
              <div className="numeric text-3xl sm:text-4xl font-bold text-[#3E2E65]">{v}</div>
              <div className="mt-1 text-sm text-[#1A1330]/55">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* final CTA */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 py-16 sm:py-20">
        <div className="relative overflow-hidden rounded-3xl border border-black/5 bg-white px-6 sm:px-12 py-12 sm:py-16 text-center">
          <div className="absolute -top-20 -right-16 h-64 w-64 rounded-full opacity-60" style={{ background: "radial-gradient(circle, rgba(167,198,63,0.22), transparent 65%)" }} />
          <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full opacity-60" style={{ background: "radial-gradient(circle, rgba(62,46,101,0.18), transparent 65%)" }} />
          <h2 className="relative text-3xl sm:text-5xl font-bold tracking-tight">Start investing in minutes</h2>
          <p className="relative mt-3 text-[#1A1330]/65 max-w-xl mx-auto">Open your account, fund it in USD, QAR, EUR and more, and access the world's markets — the halal way.</p>
          <Button size="lg" className="relative mt-7 rounded-full px-8 h-12 text-base" onClick={go} data-testid="cta-get-started">
            {t("ld_get_started")} <ArrowRight className="h-5 w-5 ms-1.5" />
          </Button>
        </div>
      </section>

      {/* footer */}
      <footer className="border-t border-black/5 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <Logo className="h-20 w-auto" />
            <div className="flex items-center gap-2 text-xs text-[#3E2E65]" data-testid="footer-ssl">
              <Lock className="h-3.5 w-3.5" /> {t("ld_secured")} · Backed up continuously
            </div>
            <div className="flex items-center gap-5 text-sm text-[#1A1330]/60">
              <button onClick={go} className="hover:text-[#3E2E65]">{t("ld_sign_in")}</button>
              <button onClick={() => navigate("/learn")} className="hover:text-[#3E2E65]" data-testid="footer-learn-link">Learn</button>
              {NAV.map(([n, key, to]) => (
                <button key={n} onClick={() => navigate(to)} className="hover:text-[#3E2E65]"
                  data-testid={`footer-${n.toLowerCase()}-link`}>{t(key)}</button>
              ))}
              <button onClick={() => navigate("/shariah-board")} className="hover:text-[#3E2E65]" data-testid="footer-board-link">Shariah board</button>
              <button onClick={() => navigate("/top-halal-stocks")} className="hover:text-[#3E2E65]" data-testid="footer-screener-link">Halal screener</button>
              <button onClick={() => navigate("/halal-leaderboards")} className="hover:text-[#3E2E65]" data-testid="footer-leaderboards-link">Rankings</button>
              <button onClick={() => navigate("/chart-lens")} className="hover:text-[#3E2E65]" data-testid="footer-chart-lens-link">Chart AI</button>
              <button onClick={go} className="hover:text-[#3E2E65]">Support</button>
            </div>
          </div>
          <p className="text-xs text-[#1A1330]/45 text-center mt-6">
            © {new Date().getFullYear()} Tharwy. Investing involves risk, including possible loss of capital. Simulated demo environment — not investment advice.
          </p>
        </div>
      </footer>
    </div>
  );
}
